import { useLocation, useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MousePointer2, Rocket, Search, Sparkles } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { Badge } from '@/components/shadcn/badge';
import { Input } from '@/components/shadcn/input';
import { Separator } from '@/components/shadcn/separator';
import { SidebarTrigger } from '@/components/shadcn/sidebar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useCan } from '@/lib/permissions';
import { api } from '@/lib/api';
import { TopUpDialog } from '@/components/billing/TopUpDialog';

const ROUTE_LABEL_KEYS: Record<string, string> = {
  dashboard: 'dashboard',
  customers: 'customers',
  vendors: 'vendors',
  invoices: 'invoices',
  quotations: 'quotations',
  bills: 'bills',
  expenses: 'expenses',
  payments: 'payments',
  bank: 'bank',
  accounts: 'chart_of_accounts',
  journal: 'journal',
  employees: 'employees',
  payroll: 'payroll',
  subscriptions: 'subscriptions',
  contracts: 'contracts',
  reports: 'reports',
  settings: 'settings',
  users: 'users',
};

export function Topbar({
  agentOpen,
  onAgentToggle,
}: {
  agentOpen: boolean;
  onAgentToggle: (v: boolean) => void;
}) {
  const { t } = useI18n();
  const { entitlements, org, user } = useAuth();
  const can = useCan();

  /*
    ONBOARDING, OFFERED WHILE IT IS STILL WORTH OFFERING.

    Beside the agent, and only while this workspace is nearly empty. An owner
    looking at a dashboard of zeroes has one useful next step and it is not on
    that dashboard, so the way back to the three doors sits where they are
    already looking.

    It leaves on its own. Once there are records in here the offer is noise,
    and Onboarding keeps its permanent place in the sidebar for the afternoon
    in March when last year's books finally arrive.

    Asked of the books rather than of `onboarded_at`, because somebody who
    pressed Skip on day one is precisely who this is for. `staleTime` is a
    minute: this changes when a person imports something, not between renders.
  */
  const canSetUp = user?.role === 'Owner' || user?.role === 'Admin';
  const { data: setup } = useQuery<{ thin: boolean }>({
    queryKey: ['org', 'setup-state', org?.id],
    queryFn: () => api<{ thin: boolean }>('/org/setup-state'),
    staleTime: 60_000,
    enabled: canSetUp,
  });
  const offerOnboarding = canSetUp && setup?.thin === true;
  const location = useLocation();
  const navigate = useNavigate();

  /* One dialog, opened from two badges below: low credits and none left. */
  const [topUpOpen, setTopUpOpen] = useState(false);

  /*
    Days left, rounded UP and computed from the date the server already sends.

    Up, because a trial with eleven hours left is on its last day and saying
    "0 days" while somebody can still use the product is a lie in the direction
    that costs us a customer. Null when there is no date rather than a guessed
    zero: a badge that invents a deadline is worse than one that shows the
    standing offer.

    No timer and no re-render on a schedule. A page open across midnight shows a
    number that is one out until the next navigation, and adding an interval to
    fix that would cost a render a second, for every user, all day, to correct a
    number nobody is looking at while it changes.
  */
  const trialDaysLeft = useMemo(() => {
    const ends = entitlements?.trial_ends_at;
    if (!ends) return null;
    const ms = new Date(ends).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    return Math.max(0, Math.ceil(ms / 86_400_000));
  }, [entitlements?.trial_ends_at]);

  const crumbLabel = useMemo(() => {
    const seg = location.pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
    const key = ROUTE_LABEL_KEYS[seg] ?? seg;
    return t(key);
  }, [location.pathname, t]);


  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border/70 bg-card/80 px-3 backdrop-blur-xl supports-[backdrop-filter]:bg-card/65 sm:px-5">
      <SidebarTrigger className="size-8 text-muted-foreground" />
      <Separator orientation="vertical" className="me-1 hidden !h-4 bg-border sm:block" />

      {/* Breadcrumb: which tenant, then where you are inside it. */}
      {/* Where you are, and nothing else. Which COMPANY you are in moved to
          the sidebar header, next to the mark — it belongs beside the product
          identity, and having it here as well meant two controls for one fact. */}
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-sm">
        <span className="truncate font-medium tracking-tight">{crumbLabel}</span>
      </nav>

      <div className="relative ms-auto hidden w-full max-w-sm lg:block">
        <Search className="pointer-events-none absolute start-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/70" />
        <Input
          placeholder={t('search')}
          aria-label={t('search')}
          className="h-8 border-transparent bg-muted/60 ps-9 pe-14 text-sm shadow-none transition-colors hover:bg-muted focus-visible:border-ring focus-visible:bg-card dark:bg-muted/40 dark:hover:bg-muted/60"
        />
        <kbd className="pointer-events-none absolute end-2 top-1/2 hidden -translate-y-1/2 rounded border border-border/70 bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground xl:inline-block">
          ⌘K
        </kbd>
      </div>

      <div className="flex items-center gap-1.5 ms-auto lg:ms-0">
        {/*
          ONBOARDING, UP HERE RATHER THAN IN THE RAIL.

          It used to be a row three levels down under Workspace, beside Billing
          and Settings, where it read as configuration. It is the opposite of
          configuration: it is the one screen that offers to fill an empty
          company, and the moment it is worth anything is the moment somebody
          is looking at empty charts and wondering whether this thing works.

          A rocket, not a sparkle. The sparkle is this product's mark for the AI
          agent (it is on the Agent button two steps to the right of here and in
          the logo itself), so wearing it for a second, unrelated meaning made
          the one thing look like the other.

          Gated on `settings.write`, the same permission the import endpoints
          check, so an employee who could not act on it is not shown it.
        */}
        {can('settings.write') && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('onboarding')}
            title={t('onboarding')}
            onClick={() => navigate('/start')}
          >
            <Rocket className="size-4" />
          </Button>
        )}

        {/* No trial countdown up here — the header is for where you are, not
            for selling. Settings → Billing states the trial and its end date.
            The one badge that stays is the lockout: when writes are blocked the
            user needs to know why, right where they hit it. */}
        {/*
          THE TRIAL, COUNTING DOWN, and the only piece of selling allowed here.

          It said "Activate now: 12 extra days free" and said it on day one and
          on the last day alike. A fixed string is not an offer, it is a
          decoration: it carries no reason to act today rather than next week,
          and by the time acting matters it has been read so often it is
          invisible.

          So it counts. "9 days left" is a fact about their workspace, it
          changes, and the change is the message.

          WHAT RIDES ALONG BESIDE THE NUMBER IS NO LONGER AN OFFER. It used to
          be "12 more free if you activate", and this badge is painted on every
          page for every trialing tenant, so that string was the product's most
          repeated promise at the moment the twelve bonus days stopped being
          given automatically (AUTOMATIC_OFFERS in backend/src/lib/pricing.ts).
          It is now the plain next step, "Choose a plan": still a reason to act,
          and one nothing downstream can fail to honour. The only discount
          anybody gets now comes from a code they type, on the billing page,
          which is where its worth can be checked before it is quoted.

          THE LAST THREE DAYS LOOK DIFFERENT. Not red: nothing is broken and
          nothing is being taken away yet, and a red badge for a trial ending on
          schedule is the product crying wolf about its own pricing. Warm, and
          the amber is spent on the one case where a person still has time to
          act and might not know it.

          Both disappear the moment they subscribe. A customer who has paid,
          still being shown an activation offer, is the sort of detail that
          makes a product feel like it is not paying attention.
        */}
        {entitlements?.open_trial && !entitlements.subscribed && (
          <Badge
            asChild
            variant="outline"
            className={cn(
              'cursor-pointer px-2.5 py-1 font-normal',
              trialDaysLeft !== null && trialDaysLeft <= 3
                ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-500'
                : 'border-foreground/25 bg-foreground/5 text-foreground',
            )}
          >
            <button type="button" onClick={() => navigate('/billing')} title={t('trial_offer_badge')}>
              {trialDaysLeft === null
                ? t('trial_offer_badge')
                : trialDaysLeft <= 0
                  ? t('trial_last_day')
                  : `${t('trial_days_left').replace('{n}', String(trialDaysLeft))} · ${t('trial_offer_short')}`}
            </button>
          </Badge>
        )}

        {/*
          CREDITS, and the one button that fixes them.

          Two states, one badge slot: running low, and gone. Both open the same
          top-up dialog, because at the moment somebody notices the warning the
          only useful next thing is the thing that resolves it. Sending them to
          a billing page to find a section to find a button is three steps of
          nothing.
        */}
        {entitlements && (entitlements.credits_remaining ?? 1) <= 0 && (
          <Badge
            asChild
            variant="outline"
            className="cursor-pointer border-destructive/45 bg-destructive/10 px-2.5 py-1 font-normal text-destructive"
          >
            <button type="button" onClick={() => setTopUpOpen(true)}>
              {t('credits_out_badge')}
            </button>
          </Badge>
        )}
        {entitlements?.credits_low && (entitlements.credits_remaining ?? 0) > 0 && (
          <Badge
            asChild
            variant="outline"
            className="cursor-pointer border-amber-500/45 bg-amber-500/10 px-2.5 py-1 font-normal text-amber-700 dark:text-amber-500"
          >
            <button type="button" onClick={() => setTopUpOpen(true)}>
              {t('credits_low_badge')}
            </button>
          </Badge>
        )}

        {/*
          The grace countdown: still writing, but on a clock.

          `grace_days_left` comes from the server, which is the same number the
          dunning email quotes and the same deadline the entitlement check
          enforces. A client that counted its own days would drift from both.
        */}
        {entitlements?.can_write && (entitlements.grace_days_left ?? 0) > 0 && (
          <Badge
            asChild
            variant="outline"
            className="cursor-pointer border-amber-500/45 bg-amber-500/10 px-2.5 py-1 font-normal text-amber-700 dark:text-amber-500"
          >
            <button type="button" onClick={() => navigate('/billing')}>
              {entitlements.grace_days_left === 1
                ? t('grace_banner_one')
                : t('grace_banner_days').replace('{days}', String(entitlements.grace_days_left))}
            </button>
          </Badge>
        )}

        {entitlements && !entitlements.can_write && (
          <Badge
            asChild
            variant="outline"
            className="cursor-pointer border-destructive/45 bg-destructive/10 px-2.5 py-1 font-normal text-destructive"
          >
            <button type="button" onClick={() => navigate('/billing')}>
              {t('billing_action_needed')}
            </button>
          </Badge>
        )}


        {/*
          The agent, where the notification bell used to be.

          There were no notifications to deliver — the bell had a permanent
          unread dot and opened nothing, which is worse than an empty inbox. The
          agent is the thing people will actually reach for from any page, so it
          gets the position.
        */}
        {offerOnboarding ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 px-2.5"
                onClick={() => navigate('/start')}
                data-testid="onboarding-shortcut"
              >
                <Sparkles className="size-4" />
                <span className="hidden sm:inline">{t('onboarding')}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('onboarding_offer')}</TooltipContent>
          </Tooltip>
        ) : null}

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={agentOpen ? 'secondary' : 'ghost'}
              size="sm"
              className="gap-1.5 px-2.5"
              onClick={() => onAgentToggle(!agentOpen)}
              aria-pressed={agentOpen}
              data-testid="agent-toggle"
            >
              <MousePointer2 className="size-4" />
              <span className="hidden sm:inline">{t('agent')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('agent')}</TooltipContent>
        </Tooltip>

        {/* No avatar menu here. It is the sidebar footer row — one account
            control, in the place people already reach for it. */}
      </div>
      <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} />
    </header>
  );
}
